// ============================================================================
// NPSDBusinessScreen.tsx - Main NPSD Business Screen Component
// Converted from Vue 2 Vue-NPSDBusiness.ts
//
// Orchestrating container screen that composes all business sub-components.
// Manages edit/save/update/cancel lifecycle, record locking, address validation,
// connection search modals, view entity modals, and case report integration.
// ============================================================================

import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useNPSDEntity } from '@/hooks/useNPSDEntity';
import { NPSDTabBar } from '@/components/npsd/NPSDTabBar';
import { NPSDWarningsDisplay } from '@/components/npsd/NPSDWarningsDisplay';
import { NPSDBusinessDetails } from '@/components/npsd/NPSDBusinessDetails';
import { NPSDBusinessConnections } from '@/components/npsd/NPSDBusinessConnections';
import { NPSDBusinessHistory } from '@/components/npsd/NPSDBusinessHistory';
import { NPSDAttachments } from '@/components/npsd/NPSDAttachments';
import { NPSDWarnings } from '@/components/npsd/NPSDWarnings';
import { NPSDAuditLog } from '@/components/npsd/NPSDAuditLog';
import { ConfirmModal } from '@/components/ConfirmModal';
import { SearchModal } from '@/components/modals/SearchModal';
import { PageModal } from '@/components/modals/PageModal';
import { NPSDPersonScreen } from '@/components/npsd/NPSDPersonScreen';
import { NPSDVehicleScreen } from '@/components/npsd/NPSDVehicleScreen';
import { NPSDClientScreenWrapper } from '@/components/npsd/NPSDClientScreen';
import {
  refreshPage,
  refreshWithParams,
  navigateToRecord,
  navigateToMapByAddress,
} from '@/services/navigation';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import moment from 'moment';
import { currentUserHasPermission, hasValue, hasStringValue, getModuleType } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';

// ============================================================================
// External Declarations
// ============================================================================

let currentBusiness = (window as any).currentBusiness;
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
import { RelType } from '@/models/other-entities';
import { getAuthObject } from '@/utils/auth';


const setNPSDItemLocked = _wg('setNPSDItemLocked');
const isBasePageCaseReport = _wg('isBasePageCaseReport');
const updateNPSDItemForReport = _wg('updateNPSDItemForReport');

// ============================================================================
// Props Interface
// ============================================================================

interface NPSDBusinessScreenProps {
  businessId?: number | string | null;
  isModal?: boolean;
  isCaseReportMode?: boolean;
  isUnknown?: boolean;
  startInEditMode?: boolean;
  onLoaded?: (business: any) => void;
  onSaved?: (business: any) => void;
  onUpdated?: (business: any) => void;
  onClose?: () => void;
  onConnectionAdded?: (type: string, connection: any) => void;
  onConnectionRemoved?: (type: string, connection: any) => void;
  onViewReport?: (reportId: number) => void;
}

// ============================================================================
// Component
// ============================================================================

export function NPSDBusinessScreen({
  businessId = null,
  isModal = false,
  isCaseReportMode: isCaseReportModeProp = false,
  isUnknown = false,
  startInEditMode = false,
  onLoaded,
  onSaved,
  onUpdated,
  onClose,
  onConnectionAdded,
  onConnectionRemoved,
  onViewReport,
}: NPSDBusinessScreenProps) {
  const { theme } = useTheme();

  // ── Core State ──────────────────────────────────────────────────────
  const [business, setBusiness] = useState<any>(null);
  const [originalBusiness, setOriginalBusiness] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('Business');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaveMode, setIsSaveMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isCaseReportMode, setIsCaseReportMode] = useState(isCaseReportModeProp);

  // ── Option Types ────────────────────────────────────────────────────
  const [phoneOptionTypes, setPhoneOptionTypes] = useState<any[]>([]);
  const [emailOptionTypes, setEmailOptionTypes] = useState<any[]>([]);

  // ── Delete Confirm Modal ────────────────────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    item: null as any,
    type: '',
  });

  // ── Cancel Confirm Modal ────────────────────────────────────────────
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  // ── useNPSDEntity hook ──────────────────────────────────────────────
  const {
    showConnectionSearchModal, connectionSearchType, openConnectionSearch, closeConnectionSearchModal,
    showViewPersonModal, viewPersonId, setShowViewPersonModal,
    showViewVehicleModal, viewVehicleId, setShowViewVehicleModal,
    showViewClientModal, viewClientId, setShowViewClientModal,
    openViewPersonModal, openViewVehicleModal, openViewClientModal,
    handleConnectionView,
    validateAndFillAddress,
    ensureConnectionsNewPopulated,
    handleAttachmentUpload,
  } = useNPSDEntity({
    entityType: 'business',
    setEntity: setBusiness,
    connectionTypes: ['people', 'vehicles', 'clients'],
  });

  // ── Role Dialog for Person Connections ──────────────────────────────
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [pendingConnectionItem, setPendingConnectionItem] = useState<any>(null);
  const [connectionRole, setConnectionRole] = useState('');

  // ── Refs ─────────────────────────────────────────────────────────────
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const businessRef = useRef<any>(business);
  const isLockedRef = useRef(isLocked);

  // Keep refs in sync (synchronous assignment during render, not useEffect,
  // so that async callbacks always see the latest value)
  businessRef.current = business;
  isLockedRef.current = isLocked;

  // ── Computed Values ─────────────────────────────────────────────────

  const isNew = useMemo(() => {
    return !business || !business.id;
  }, [business]);

  const canEdit = useMemo(() => {
    if (!business || !business.id) return false;
    if (isEditMode) return false;
    if (!currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_UPDATE)) return false;
    return getAuthObject().company_id === business.createdByCompanyId;
  }, [business, isEditMode]);

  const canSave = useMemo(() => {
    if (!isNew) return false;
    if (isCaseReportMode) return false;
    return currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_SAVE);
  }, [isNew, isCaseReportMode]);

  const canSaveToReport = useMemo(() => {
    if (!isNew) return false;
    if (!isCaseReportMode) return false;
    return currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_SAVE);
  }, [isNew, isCaseReportMode]);

  const canUpdate = useMemo(() => {
    if (!business || !business.id) return false;
    if (!isEditMode) return false;
    return currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_UPDATE);
  }, [business, isEditMode]);

  const canCancel = canUpdate;

  const enableFormFields = useMemo(() => {
    return isEditMode || isSaveMode;
  }, [isEditMode, isSaveMode]);

  const showAddConnectionButton = enableFormFields;
  const showAddAttachmentButton = enableFormFields;

  const connectionSearchTypeLabel = useMemo(() => {
    const labels: Record<string, string> = {
      'person': 'Person',
      'vehicle': 'Vehicle',
      'client': 'Client',
    };
    return labels[connectionSearchType] || '';
  }, [connectionSearchType]);

  const locationExists = useMemo(() => {
    return hasValue(business?.locFormattedAddress) && business.locFormattedAddress !== '';
  }, [business?.locFormattedAddress]);

  const connectionsCount = useMemo(() => {
    if (!business?.connections) return 0;
    const c = business.connections;
    return (c.people?.length || 0) + (c.vehicles?.length || 0) + (c.clients?.length || 0);
  }, [business?.connections]);

  const tabsConfig = useMemo(() => {
    return [
      { id: 'Business', label: 'Business' },
      { id: 'Connections', label: 'Connections', badge: connectionsCount },
      { id: 'History', label: 'History' },
      { id: 'Attachments', label: 'Attachments', badge: business?.attachmentList?.length || 0 },
      { id: 'Warnings', label: 'Warnings', badge: business?.warnings?.length || 0 },
      { id: 'AuditLog', label: 'Audit Log' },
    ];
  }, [connectionsCount, business?.attachmentList?.length, business?.warnings?.length]);

  // ── Helper Functions ────────────────────────────────────────────────

  const isDataEqual = useCallback((original: any, current: any): boolean => {
    return JSON.stringify(original) === JSON.stringify(current);
  }, []);

  const updateGlobalCurrentBusiness = useCallback((biz: any) => {
    if (isModal) return;
    currentBusiness = biz;
    (window as any).currentBusiness = biz;
    if (typeof App !== 'undefined' && App) {
      App.originalBusinessResult = JSON.parse(JSON.stringify(biz));
    }
  }, [isModal]);

  // ── Load / Init ─────────────────────────────────────────────────────

  const loadBusiness = useCallback((id: string | number) => {
    setIsLoading(true);
    api.getNPSDItemById(id.toString(), 'business', (result: any) => {
      const biz = new (window as any).Business(result);
      setBusiness(biz);
      setOriginalBusiness(JSON.parse(JSON.stringify(biz)));
      setIsEditMode(false);
      setIsSaveMode(false);
      setIsLoading(false);

      updateGlobalCurrentBusiness(biz);

      if (startInEditMode) {
        setIsEditMode(true);
        if (biz.id) {
          setNPSDItemLocked(biz.id.toString(), true, 'business', () => {
            setIsLocked(true);
          });
        }
      }

      onLoaded?.(biz);
    });
  }, [startInEditMode, updateGlobalCurrentBusiness, onLoaded]);

  const initNewBusiness = useCallback(() => {
    const biz = new (window as any).Business('');
    if (isUnknown) {
      biz.businessName = 'Unknown';
      biz.businessType = 'Unknown';
    }
    setBusiness(biz);
    setOriginalBusiness(JSON.parse(JSON.stringify(biz)));
    updateGlobalCurrentBusiness(biz);

    if (isModal) {
      setIsCaseReportMode(true);
    }
    setIsSaveMode(true);
    setIsEditMode(false);
    setIsLoading(false);
  }, [isUnknown, isModal, updateGlobalCurrentBusiness]);

  // ── Effects ─────────────────────────────────────────────────────────

  // Load business on mount / businessId change
  useEffect(() => {
    if (businessId) {
      loadBusiness(businessId);
    } else {
      initNewBusiness();
    }
  }, [businessId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep global currentBusiness in sync (non-modal only)
  useEffect(() => {
    if (!isModal && business) {
      currentBusiness = business;
      (window as any).currentBusiness = business;
    }
  }, [business, isModal]);

  // Load option types on mount
  useEffect(() => {
    const pot = (window as any).PhoneOptionTypes;
    const eot = (window as any).EmailOptionTypes;
    setPhoneOptionTypes(typeof pot !== 'undefined' ? pot : []);
    setEmailOptionTypes(typeof eot !== 'undefined' ? eot : []);
  }, []);

  // Cleanup: unlock business on unmount
  useEffect(() => {
    return () => {
      if (isLockedRef.current && businessRef.current?.id) {
        setNPSDItemLocked(businessRef.current.id.toString(), false, 'business', () => {});
      }
    };
  }, []);

  // ── Edit / Lock / Unlock ────────────────────────────────────────────

  const lockBusiness = useCallback(() => {
    if (business?.id) {
      setNPSDItemLocked(business.id.toString(), true, 'business', () => {
        setIsLocked(true);
      });
    }
  }, [business?.id]);

  const unlockBusiness = useCallback(() => {
    if (business?.id && isLocked) {
      setNPSDItemLocked(business.id.toString(), false, 'business', () => {
        setIsLocked(false);
      });
    }
  }, [business?.id, isLocked]);

  const edit = useCallback(() => {
    setIsEditMode(true);
    lockBusiness();
  }, [lockBusiness]);

  // ── Cancel Edit ─────────────────────────────────────────────────────

  const doCancelEdit = useCallback(() => {
    setCancelConfirmOpen(false);
    unlockBusiness();

    if (!isDataEqual(originalBusiness, business)) {
      if (typeof App !== 'undefined') App.originalBusinessResult = null;
      if (!isModal) {
        refreshPage();
      }
    }

    setIsEditMode(false);
  }, [unlockBusiness, isDataEqual, originalBusiness, business, isModal]);

  const cancelEdit = useCallback(() => {
    if (isModal) {
      unlockBusiness();
      if (typeof App !== 'undefined') App.originalBusinessResult = null;
      onClose?.();
      return;
    }

    if (!isDataEqual(originalBusiness, business)) {
      setCancelConfirmOpen(true);
    } else {
      doCancelEdit();
    }
  }, [isModal, unlockBusiness, onClose, isDataEqual, originalBusiness, business, doCancelEdit]);

  const closeCancelConfirm = useCallback(() => {
    setCancelConfirmOpen(false);
  }, []);

  // ── Validate ────────────────────────────────────────────────────────

  const validateBusiness = useCallback((): boolean => {
    if (!hasStringValue(business.businessName)) {
      App?.showPopupNotification('Business name is required', 'warning');
      return false;
    }
    if (!hasStringValue(business.businessType)) {
      App?.showPopupNotification('Business type is required', 'warning');
      return false;
    }
    return true;
  }, [business]);

  // ── Update Attachments ──────────────────────────────────────────────

  const updateAttachments = useCallback((biz: any, origBiz: any) => {
    if (!biz.attachmentList || !origBiz?.attachmentList) return;

    for (let i = 0; i < biz.attachmentList.length; i++) {
      const item = biz.attachmentList[i];
      const originalItem = origBiz.attachmentList.find((d: any) => d.id === item.id);

      if ((originalItem && item.title !== originalItem.title) || !originalItem) {
        api.updateAttachment(item.title, item.id, () => {});
      }
    }
  }, []);

  // ── Save / Update ───────────────────────────────────────────────────

  const saveOrUpdate = useCallback(async () => {
    if (!validateBusiness()) return;

    const addressFields = await validateAndFillAddress(businessRef.current?.locFormattedAddress, originalBusiness);
    if (!addressFields) return;

    // Merge address fields into the business (state update is async, so merge directly)
    const currentBiz = { ...businessRef.current, ...addressFields };
    const populatedBiz = ensureConnectionsNewPopulated(currentBiz);

    setIsSaving(true);
    setIsEditMode(false);

    if (!populatedBiz.id) {
      // Save new business
      api.saveNPSDItem(populatedBiz, 'business', (result: any) => {
        if (typeof App !== 'undefined') App.originalBusinessResult = null;
        App?.showPopupNotification('Business saved successfully', 'success');

        if (!isModal && !isBasePageCaseReport()) {
          setIsSaving(false);
          setOriginalBusiness(null);
          refreshWithParams(result.id);
        } else if (isBasePageCaseReport()) {
          const addToReport = (window as any).NPSDItemToReport;
          if (typeof addToReport === 'function') addToReport('lnkRptAddBusiness', result.id);
          (window as any).CaseRpt?.currentMDTRptController?.closeInvolvedSearchModal();
        } else {
          setIsSaving(false);
          setOriginalBusiness(null);
          setBusiness((prev: any) => ({ ...prev, id: result.id }));
          onSaved?.({ ...populatedBiz, id: result.id });
        }
      });
    } else {
      // Update attachments if changed
      updateAttachments(populatedBiz, originalBusiness);

      // Update existing business
      api.updateNPSDItem(populatedBiz, 'business', (result: any) => {
        setIsSaving(false);

        // Handle case report context
        if ((window as any).CaseRpt?.currentMDTRptController?.isSearchOpen()) {
          (window as any).CaseRpt?.currentMDTRptController.closeInvolvedSearchModal();
        }

        setOriginalBusiness(null);
        if (typeof App !== 'undefined') App.originalBusinessResult = null;
        App?.showPopupNotification('Business updated successfully', 'success');

        if (typeof App !== 'undefined') App.pauseItemChangeCheck = true;

        if (isModal) {
          onUpdated?.(populatedBiz);
          onClose?.();
        } else if (!isBasePageCaseReport()) {
          refreshPage();
        }

        if (isBasePageCaseReport()) {
          updateNPSDItemForReport(RelType.business, populatedBiz);
        }
      }, (error: any) => {
        setIsSaving(false);
        setIsEditMode(true);
        App?.showPopupNotification('Failed to update business', 'error');
      });
    }
  }, [validateBusiness, validateAndFillAddress, ensureConnectionsNewPopulated, isModal, originalBusiness, updateAttachments, onSaved, onUpdated, onClose]);

  // ── Business Change Handler (from details sub-component) ────────────

  const handleBusinessChange = useCallback((updatedBusiness: any) => {
    setBusiness(updatedBusiness);
  }, []);

  // ── Map View ────────────────────────────────────────────────────────

  const viewMap = useCallback(() => {
    if (!business?.locFormattedAddress) return;
    navigateToMapByAddress(business.locFormattedAddress);
  }, [business?.locFormattedAddress]);

  // ── Warning Management ──────────────────────────────────────────────

  const handleWarningsChange = useCallback((updatedWarnings: any[]) => {
    setBusiness((prev: any) => ({ ...prev, warnings: updatedWarnings }));
  }, []);

  // ── Attachment Management ───────────────────────────────────────────

  const addAttachment = useCallback(() => {
    if (!isSaveMode && !isEditMode) {
      edit();
    }
    if (attachmentInputRef.current) {
      attachmentInputRef.current.click();
    }
  }, [isSaveMode, isEditMode, edit]);

  const removeAttachment = useCallback((attachment: any) => {
    setDeleteConfirm({
      open: true,
      item: attachment,
      type: 'attachment',
    });
  }, []);

  const confirmRemoveAttachment = useCallback(() => {
    if (deleteConfirm.item && deleteConfirm.type === 'attachment') {
      setBusiness((prev: any) => ({
        ...prev,
        attachmentList: (prev.attachmentList || []).filter((a: any) => a.id !== deleteConfirm.item.id),
        attachmentInfoList: (prev.attachmentInfoList || []).filter((a: any) => a.id !== deleteConfirm.item.id),
      }));
    }
    setDeleteConfirm({ open: false, item: null, type: '' });
  }, [deleteConfirm]);

  const closeDeleteConfirm = useCallback(() => {
    setDeleteConfirm({ open: false, item: null, type: '' });
  }, []);

  // ── Add Connection to Entity ────────────────────────────────────────

  const addConnectionToEntity = useCallback((type: string, item: any, role?: string) => {
    const pluralType = type === 'person' ? 'people' :
                       type === 'business' ? 'businesses' :
                       type === 'client' ? 'clients' :
                       type + 's';

    let connection: any = { id: item.id };

    if (type === 'person') {
      connection = {
        id: item.id,
        firstName: item.firstName,
        lastName: item.lastName,
        dob: item.dob,
        roles: role || '',
      };
    } else if (type === 'vehicle') {
      connection = {
        id: item.id,
        plateNumber: item.plateNumber,
        plateState: item.plateState,
        make: item.make,
        model: item.model,
        color: item.color,
        vin: item.vin,
      };
    } else if (type === 'client') {
      connection = {
        id: item.id,
        name: item.name,
        locCity: item.locCity,
        locState: item.locState,
        website: item.website,
        roles: '',
      };
    }

    setBusiness((prev: any) => {
      const updatedConnections = { ...prev.connections };
      const updatedConnectionsNew = { ...(prev.connectionsNew || { people: [], vehicles: [], clients: [] }) };

      if (!updatedConnections[pluralType]) updatedConnections[pluralType] = [];
      if (!updatedConnectionsNew[pluralType]) updatedConnectionsNew[pluralType] = [];

      updatedConnections[pluralType] = [...updatedConnections[pluralType], connection];
      updatedConnectionsNew[pluralType] = [...updatedConnectionsNew[pluralType], connection];

      return {
        ...prev,
        connections: updatedConnections,
        connectionsNew: updatedConnectionsNew,
      };
    });

    App?.showPopupNotification('Connection added.', 'success');
    closeConnectionSearchModal();
  }, [closeConnectionSearchModal]);

  // ── Handle Connection Select ────────────────────────────────────────

  const handleConnectionSelect = useCallback((item: any) => {
    const type = connectionSearchType;
    const pluralType = type === 'person' ? 'people' :
                       type === 'business' ? 'businesses' :
                       type === 'client' ? 'clients' :
                       type + 's';

    const existingArray = business?.connections?.[pluralType] || [];
    const isDuplicate = existingArray.some((c: any) => c.id === item.id);

    if (isDuplicate) {
      App?.showPopupNotification('This connection already exists.', 'warning');
      return;
    }

    // For person connections, show dialog to enter Title/Role
    if (type === 'person') {
      setPendingConnectionItem(item);
      setConnectionRole('');
      closeConnectionSearchModal();
      setShowRoleDialog(true);
      return;
    }

    // Add connection directly for non-person types
    addConnectionToEntity(type, item);
  }, [connectionSearchType, business?.connections, closeConnectionSearchModal, addConnectionToEntity]);

  // ── View Connection from Existing Connections Panel ──────────────────

  const viewNpsdItem = useCallback((type: string, item: any) => {
    const id = typeof item === 'object' ? item.id : item;
    switch (type) {
      case 'person': openViewPersonModal(id); break;
      case 'vehicle': openViewVehicleModal(id); break;
      case 'client': openViewClientModal(id); break;
    }
  }, [openViewPersonModal, openViewVehicleModal, openViewClientModal]);


  // ── View Modal Close Handlers ───────────────────────────────────────

  const closeViewPersonModal = useCallback(() => {
    setShowViewPersonModal(false);
    if (business?.id) loadBusiness(business.id);
  }, [business?.id, loadBusiness, setShowViewPersonModal]);

  const closeViewVehicleModal = useCallback(() => {
    setShowViewVehicleModal(false);
    if (business?.id) loadBusiness(business.id);
  }, [business?.id, loadBusiness, setShowViewVehicleModal]);

  const closeViewClientModal = useCallback(() => {
    setShowViewClientModal(false);
    if (business?.id) loadBusiness(business.id);
  }, [business?.id, loadBusiness, setShowViewClientModal]);

  // ── Role Dialog ─────────────────────────────────────────────────────

  const confirmRoleDialog = useCallback(() => {
    if (pendingConnectionItem) {
      addConnectionToEntity('person', pendingConnectionItem, connectionRole);
    }
    setShowRoleDialog(false);
    setPendingConnectionItem(null);
    setConnectionRole('');
  }, [pendingConnectionItem, connectionRole, addConnectionToEntity]);

  const closeRoleDialog = useCallback(() => {
    setShowRoleDialog(false);
    setPendingConnectionItem(null);
    setConnectionRole('');
  }, []);

  // ── Connection Removed Handler ──────────────────────────────────────

  const handleConnectionRemoved = useCallback((type: string, connection: any) => {
    onConnectionRemoved?.(type, connection);
  }, [onConnectionRemoved]);

  // ── History Event ───────────────────────────────────────────────────

  const handleViewReport = useCallback((reportId: number) => {
    onViewReport?.(reportId);
  }, [onViewReport]);

  // ── Render ──────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className={`npsd-entity-screen npsd-business-screen${theme ? ' ' + theme : ''}`}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`npsd-entity-screen npsd-business-screen${theme ? ' ' + theme : ''}`}>
      {/* Header Section */}
      <div className="profile-card-header">
        <div className="profile-cover-details">
          <div className="profile-info">
            <h3 className="business-title">
              {business.businessName || 'New Business'}
              {business.branchName && <span> - {business.branchName}</span>}
            </h3>
          </div>
        </div>
        <div className="action-buttons">
          {canEdit && (
            <button type="button" className="btn btn-header" onClick={edit}>Edit</button>
          )}
          {business.createdByCompanyId && getAuthObject().company_id !== business.createdByCompanyId && (
            <span className="created-by">{business.createdByCompanyName}</span>
          )}
          {canUpdate && (
            <button type="button" className="kstc-btn kstc-btn--success" onClick={saveOrUpdate} disabled={isSaving}>Update</button>
          )}
          {canCancel && (
            <button type="button" className="kstc-btn kstc-btn--danger" onClick={cancelEdit}>Cancel</button>
          )}
          {canSave && (
            <button type="button" className="kstc-btn kstc-btn--success" onClick={saveOrUpdate} disabled={isSaving}>Save</button>
          )}
          {canSaveToReport && (
            <button type="button" className="kstc-btn kstc-btn--success" onClick={saveOrUpdate} disabled={isSaving}>Save/Add</button>
          )}
        </div>
        <NPSDWarningsDisplay warnings={business.warnings || []} />
      </div>

      {/* Tab Navigation */}
      <NPSDTabBar
        tabs={tabsConfig}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Tab Content */}
      <div className="npsd-business-content business-tab-content">
        {/* Business Tab */}
        <div className="business-tab-panel" style={{ display: activeTab === 'Business' ? undefined : 'none' }}>
          <NPSDBusinessDetails
            business={business}
            isEditMode={enableFormFields}
            phoneOptionTypes={phoneOptionTypes}
            emailOptionTypes={emailOptionTypes}
            locationExists={locationExists}
            onChange={handleBusinessChange}
            onViewMap={viewMap}
          />
        </div>

        {/* Connections Tab */}
        <div className="business-tab-panel" style={{ display: activeTab === 'Connections' ? undefined : 'none' }}>
          <NPSDBusinessConnections
            business={business}
            isEditMode={enableFormFields}
            showAddButton={showAddConnectionButton}
            onAddConnection={openConnectionSearch}
            onViewConnection={viewNpsdItem}
            onConnectionRemoved={handleConnectionRemoved}
          />
        </div>

        {/* History Tab */}
        <div className="business-tab-panel" style={{ display: activeTab === 'History' ? undefined : 'none' }}>
          <NPSDBusinessHistory
            reports={business.connections ? business.connections.reports : []}
            onViewReport={handleViewReport}
          />
        </div>

        {/* Attachments Tab */}
        <div className="business-tab-panel" style={{ display: activeTab === 'Attachments' ? undefined : 'none' }}>
          <NPSDAttachments
            attachmentList={business.attachmentList || []}
            isEditMode={showAddAttachmentButton}
            showProfilePicOption={false}
            onAdd={addAttachment}
            onRemove={removeAttachment}
          />
          <input
            ref={attachmentInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleAttachmentUpload}
          />
        </div>

        {/* Warnings Tab */}
        <div className="business-tab-panel" style={{ display: activeTab === 'Warnings' ? undefined : 'none' }}>
          <NPSDWarnings
            warnings={business.warnings || []}
            isReadOnly={!enableFormFields}
            title="Warnings"
            onChange={handleWarningsChange}
          />
        </div>

        {/* Audit Log Tab */}
        <div className="business-tab-panel" style={{ display: activeTab === 'AuditLog' ? undefined : 'none' }}>
          <NPSDAuditLog
            entityId={business.id}
            entityType="business"
            title="Audit Log"
          />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteConfirm.open}
        title="Remove Item?"
        message="Are you sure you want to remove this item?"
        confirmText="Remove"
        cancelText="Cancel"
        confirmClass="danger"
        onConfirm={confirmRemoveAttachment}
        onCancel={closeDeleteConfirm}
      />

      {/* Cancel Edit Confirmation Modal */}
      <ConfirmModal
        open={cancelConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmText="Discard"
        cancelText="Keep Editing"
        confirmClass="danger"
        onConfirm={doCancelEdit}
        onCancel={closeCancelConfirm}
      />

      {/* Connection Search Modal */}
      <SearchModal
        open={showConnectionSearchModal}
        searchType={connectionSearchType as any}
        title={'Add ' + connectionSearchTypeLabel + ' Connection'}
        onSelect={handleConnectionSelect}
        onView={handleConnectionView}
        onCancel={closeConnectionSearchModal}
      />

      {/* View Person Modal */}
      <PageModal
        open={showViewPersonModal}
        title="View Person"
        modalId="businessViewPerson"
        onClose={closeViewPersonModal}
      >
        {showViewPersonModal && viewPersonId && (
          <NPSDPersonScreen
            personId={viewPersonId}
            isWindow={true}
            onClose={closeViewPersonModal}
          />
        )}
      </PageModal>

      {/* View Vehicle Modal */}
      <PageModal
        open={showViewVehicleModal}
        title="View Vehicle"
        modalId="businessViewVehicle"
        onClose={closeViewVehicleModal}
      >
        {showViewVehicleModal && viewVehicleId && (
          <NPSDVehicleScreen
            vehicleId={viewVehicleId}
            isWindow={true}
            onClose={closeViewVehicleModal}
          />
        )}
      </PageModal>

      {/* View Client Modal */}
      <PageModal
        open={showViewClientModal}
        title="View Client"
        modalId="businessViewClient"
        onClose={closeViewClientModal}
      >
        {showViewClientModal && viewClientId && (
          <NPSDClientScreenWrapper
            clientId={String(viewClientId)}
          />
        )}
      </PageModal>

      {/* Role Dialog for Person Connections */}
      <ConfirmModal
        open={showRoleDialog}
        title="Person Connection"
        confirmText="Add Connection"
        cancelText="Cancel"
        onConfirm={confirmRoleDialog}
        onCancel={closeRoleDialog}
      >
        <div className="role-dialog-content">
          {pendingConnectionItem && (
            <p>
              Adding connection to: <strong>{pendingConnectionItem.firstName} {pendingConnectionItem.lastName}</strong>
            </p>
          )}
          <div className="field-group">
            <label>Title/Role (optional)</label>
            <input
              type="text"
              className="kstc-input"
              value={connectionRole}
              onChange={(e) => setConnectionRole(e.target.value)}
              placeholder="e.g. Owner, Manager, Employee"
            />
          </div>
        </div>
      </ConfirmModal>
    </div>
  );
}

// ── Screen registry ──────────────────────────────────────────────────────
